<template>
    <v-navigation-drawer id="sidebar" :rail="effectiveRail" :width="300"
                         :temporary="isPhone" :model-value="isPhone ? drawerOpen : true"
                         class="pa-0" mobile-breakpoint="0"
                         @update:model-value="setDrawer"
                         @mouseenter="startPeek"
                         @mouseleave="endPeek">
        <v-list class="pa-0">
            <v-list-item :title="authStore?.user?.company?.company_name" class="pa-0">
                <template #title>
                    <div class="d-flex align-center w-100">
                        <v-img v-if="companyLogo" :max-height="60" :max-width="100" :src="companyLogo"
                               aspect-ratio="1/1" contain/>
                        <h5 v-if="!effectiveRail" class="text-left">{{ companyName }}</h5>
                    </div>
                </template>
            </v-list-item>
        </v-list>

        <v-toolbar v-if="!isPhone" class="bg-transparent sidebar-toggle-toolbar" height="30px">
            <v-btn :class="{'active':!rail}"
                   :aria-label="rail ? 'Expand menu' : 'Collapse menu'"
                   icon="mdi-menu"
                   @click="()=> {rail = !rail}"/>
        </v-toolbar>

        <div v-if="!effectiveRail" class="px-2 pb-1">
            <v-text-field v-model="searchQuery" density="compact" variant="solo-filled" flat
                          hide-details clearable prepend-inner-icon="mdi-magnify"
                          placeholder="Find a feature...">
                <template #append-inner>
                    <span class="text-caption text-medium-emphasis text-no-wrap shortcut-hint" role="button"
                          tabindex="0" :aria-label="'Search pages and vehicles (' + shortcutLabel + ')'"
                          :title="'Search pages and vehicles (' + shortcutLabel + ')'"
                          @keydown.enter.stop="paletteOpen = true"
                          @click.stop="paletteOpen = true">{{ shortcutLabel }}</span>
                </template>
            </v-text-field>
        </div>

        <v-list v-if="searchQuery" class="mt-1 text-left" density="compact">
            <v-list-item v-for="r in searchResults" :key="r.url + (r.title || '')"
                         :active="isActive(r.url, r.params)"
                         :prepend-icon="r.icon || 'mdi-chevron-right'"
                         :title="r.title"
                         :subtitle="r.group"
                         @click="go(r.url, r.params)"/>
            <v-list-item v-if="!searchResults.length" title="No matches" class="text-medium-emphasis"/>
        </v-list>

        <v-list v-else v-model:opened="openedModel" :value="null" class="mt-1 text-left" density="compact">
            <template v-for="(menuItem, index) in filteredMenuItems">
                <v-list-group v-if="menuItem.submenu" :value="'g' + index">
                    <template v-slot:activator="{ props }">
                        <v-list-item :prepend-icon="menuItem.icon"
                                     :title="menuItem.title"
                                     :class="{'group-has-active': containsActive(menuItem)}"
                                     v-bind="props"
                                     @click="openFromRail('g' + index)">
                            <template v-if="effectiveRail && groupBadge(menuItem)" #prepend>
                                <v-badge :color="menuCountColor(groupBadge(menuItem).severity)" dot
                                         :aria-label="groupBadge(menuItem).label">
                                    <v-icon/>
                                </v-badge>
                            </template>
                            <template v-if="!effectiveRail && !isOpen('g' + index) && groupBadge(menuItem)" #append>
                                <MenuCountChip :badge="groupBadge(menuItem)"/>
                                <v-icon/>
                            </template>
                        </v-list-item>
                    </template>

                    <template v-for="(submenuItem, subIndex) in menuItem.submenu">
                        <v-list-group v-if="submenuItem.submenu" :value="'g' + index + '-' + subIndex">
                            <template v-slot:activator="{ props }">
                                <v-list-item :title="submenuItem.title" v-bind="props"
                                             :class="{'group-has-active': containsActive(submenuItem)}">
                                    <template v-if="!isOpen('g' + index + '-' + subIndex) && groupBadge(submenuItem)" #append>
                                        <MenuCountChip :badge="groupBadge(submenuItem)"/>
                                        <v-icon/>
                                    </template>
                                </v-list-item>
                            </template>
                            <v-list-item v-for="(submenuSubItem) in submenuItem.submenu"
                                         :active="isActive(submenuSubItem.url,submenuSubItem.params)"
                                         :title="submenuSubItem.title"
                                         @click="goItem(submenuSubItem)">
                                <template v-if="badgeFor(submenuSubItem)" #append>
                                    <MenuCountChip :badge="badgeFor(submenuSubItem)"/>
                                </template>
                            </v-list-item>
                        </v-list-group>

                        <v-list-item v-else
                                     :active="isActive(submenuItem.url,submenuItem.params)"
                                     :title="submenuItem.title"
                                     @click="goItem(submenuItem)">
                            <template v-if="badgeFor(submenuItem)" #append>
                                <MenuCountChip :badge="badgeFor(submenuItem)"/>
                            </template>
                        </v-list-item>
                    </template>
                </v-list-group>

                <v-list-item v-else
                             :active="isActive(menuItem.url,menuItem.params)"
                             :prepend-icon="menuItem.icon"
                             :title="menuItem.title"
                             :value="index"
                             class="text-left"
                             @click="goItem(menuItem)">
                    <template v-if="effectiveRail && badgeFor(menuItem)" #prepend>
                        <v-badge :color="menuCountColor(badgeFor(menuItem).severity)" dot
                                 :aria-label="badgeFor(menuItem).label">
                            <v-icon/>
                        </v-badge>
                    </template>
                    <template v-if="!effectiveRail && badgeFor(menuItem)" #append>
                        <MenuCountChip :badge="badgeFor(menuItem)"/>
                    </template>
                </v-list-item>
            </template>
        </v-list>

        <template v-slot:append>
            <v-list>
                <v-tooltip :text="'Build: ' + buildVersion" location="top">
                    <template #activator="{ props }">
                        <v-list-item :prepend-avatar="logo" to="/" v-bind="props">
                            <template #title>
                                <v-img :src="logoText" :width="100" aspect-ratio="4/3" cover/>
                            </template>
                        </v-list-item>
                    </template>
                </v-tooltip>
            </v-list>
        </template>

        <v-dialog v-model="paletteOpen" max-width="560" aria-label="Search pages and vehicles">
            <v-card>
                <v-text-field v-model="paletteQuery" autofocus hide-details variant="solo" flat
                              aria-label="Search pages and vehicles"
                              role="combobox"
                              aria-autocomplete="list"
                              aria-expanded="true"
                              aria-controls="palette-listbox"
                              :aria-activedescendant="paletteResults.length ? 'palette-opt-' + paletteIndex : undefined"
                              prepend-inner-icon="mdi-magnify"
                              :placeholder="canSearchVehicles ? 'Search pages and vehicles...' : 'Search pages...'"
                              @keydown="onPaletteKey"/>
                <v-divider/>
                <v-list id="palette-listbox" role="listbox" density="compact" class="text-left py-0"
                        max-height="420" aria-label="Results">
                    <template v-for="(r, i) in paletteResults" :key="r.kind + r.url + JSON.stringify(r.params || {})">
                        <v-list-subheader v-if="i === 0 || paletteResults[i - 1].section !== r.section" role="presentation">
                            {{ r.section }}
                        </v-list-subheader>
                        <v-list-item :id="'palette-opt-' + i"
                                     role="option"
                                     :aria-selected="i === paletteIndex ? 'true' : 'false'"
                                     :active="i === paletteIndex"
                                     :prepend-icon="r.icon || 'mdi-chevron-right'"
                                     :title="r.title"
                                     :subtitle="r.subtitle"
                                     @mouseenter="paletteIndex = i"
                                     @click="choosePaletteItem(r)"/>
                    </template>
                    <v-list-item v-if="vehicleSearching" role="presentation" class="text-medium-emphasis">
                        <template #prepend>
                            <v-progress-circular indeterminate size="18" width="2" class="mr-4"/>
                        </template>
                        <v-list-item-title>Searching vehicles...</v-list-item-title>
                    </v-list-item>
                    <v-list-item v-else-if="paletteQuery && !paletteResults.length" role="presentation"
                                 title="No matches" class="text-medium-emphasis"/>
                </v-list>
                <div class="d-sr-only" aria-live="polite">{{ paletteLiveText }}</div>
                <v-divider/>
                <div class="text-caption text-medium-emphasis px-4 py-2">Up/Down to move, Enter to open, Esc to close</div>
            </v-card>
        </v-dialog>
    </v-navigation-drawer>
</template>

<script setup>
import {computed, inject, nextTick, onMounted, onUnmounted, ref, watch} from "vue";
import {useDisplay} from "vuetify";
import logo from "@/assets/images/logo-1.png";
import {useAuthStore} from "@/stores/authStore";
import {useRoute, useRouter} from 'vue-router';
import {useNavigation, isCompanyAdminRole} from "@/composables/globals";
import {send, unreadNotificationsCount} from "@/composables/apiClient";
import {combineMenuCounts, menuCountColor, useMenuCounts} from "@/composables/useMenuCounts";
import MenuCountChip from "@/components/layouts/MenuCountChip.vue";
import logoText from "@/assets/images/logo-text.png";
import adminLogo from "@/assets/images/admin-logo.jpg";


const route = useRoute();
const router = useRouter();
const authStore = useAuthStore();
const {navigateTo} = useNavigation();
const {user, configs, menus} = authStore;

// Below md the drawer is a temporary overlay opened from the TopBar hamburger.
const {smAndDown: isPhone} = useDisplay();
const drawerOpen = inject('navDrawerOpen', ref(false));
const setDrawer = (open) => {
    if (isPhone.value) drawerOpen.value = open;
};
watch(isPhone, () => drawerOpen.value = false);
watch(() => route.fullPath, () => drawerOpen.value = false);

// Navigate from the menu; on phones also close the drawer when the page is already open
const go = (url, params) => {
    navigateTo(url, params ?? {});
    if (isPhone.value) drawerOpen.value = false;
};

// Default to the expanded menu (labels visible) so new users can see every
// option; a saved sidebar preference still wins. Was previously defaulting to
// the icon-only rail, which hid what the app can do.
const rail = ref((authStore.isAuthenticated() && parseInt(configs.sidebar)) ? !parseInt(configs.sidebar) : false);

// Which expandable groups are open. While the drawer is collapsed (rail) we keep
// all groups closed so the narrow rail shows only the icon'd top-level items -
// otherwise a group's icon-less child items render as ugly text in the rail.
const openedGroups = ref([]);
watch(rail, () => {
    clearTimeout(peekTimer);
    hoverPeeking.value = false;
    openedGroups.value = [];
});

// Hover peek: temporarily expand the sidebar as overlay on mouse-over when
// the user has it collapsed. A 120ms delay prevents accidental expansion when
// the cursor passes through the rail. Rail state is not modified; effectiveRail
// drives all rendering so the hamburger button always reflects the saved pref.
const hoverPeeking = ref(false);
let peekTimer = null;

const effectiveRail = computed(() => !isPhone.value && rail.value && !hoverPeeking.value);

const openedModel = computed({
    get: () => effectiveRail.value ? [] : openedGroups.value,
    set: (value) => {
        if (!effectiveRail.value) openedGroups.value = value;
    },
});

function startPeek() {
    if (isPhone.value || !rail.value || (typeof window !== 'undefined' && window.matchMedia('(hover: none)').matches)) return;
    peekTimer = setTimeout(() => {
        hoverPeeking.value = true;
        openedGroups.value = filteredMenuItems.value
            .map((item, idx) => item.submenu ? ('g' + idx) : null)
            .filter(Boolean);
    }, 120);
}

function endPeek() {
    clearTimeout(peekTimer);
    if (!hoverPeeking.value) return;
    hoverPeeking.value = false;
    openedGroups.value = [];
    searchQuery.value = '';
}

// In the rail a group click opens the peek with that group expanded, so touch screens get it too.
function openFromRail(value) {
    if (!effectiveRail.value) return;
    clearTimeout(peekTimer);
    nextTick(() => {
        hoverPeeking.value = true;
        openedGroups.value = [value];
    });
}

// A tap outside the drawer closes a peek that no mouseleave will end.
const onOutsidePointer = (e) => {
    if (hoverPeeking.value && !e.target?.closest?.('#sidebar')) endPeek();
};

watch(route, endPeek);

const buildVersion = ref('...');
fetch('/version.txt').then(r => r.text()).then(v => buildVersion.value = v.trim()).catch(() => buildVersion.value = 'unknown');

const companyLogo = ref((user.type !== 'admin') ? authStore?.user?.company?.company_logo : adminLogo);
const companyName = ref((user.type !== 'admin') ? authStore?.user?.company?.company_name : 'SAAS ADMIN');

const admin_menu = [
    {title: "Dashboard", icon: "mdi-view-dashboard", url: "dashboard", key: "dashboard"},
    {title: "Live View", icon: "mdi-broadcast", url: "live-view", key: "live_view"},
    {
        url: "assets",
        key: "assets",
        title: "Assets",
        icon: "mdi-checkbox-multiple-blank",
        submenu: [
            {
                url: "notifications",
                title: "Notifications",
            },
            {
                url: "asset-groups",
                title: "Groups",
            },
            {
                url: "asset-types",
                title: "Types",
            },
        ]
    },
    {title: "Compliance", icon: "mdi-leaf-circle-outline", url: "ctc-compliance", key: "compliance"},
    {
        url: "devices",
        key: "devices",
        title: "Devices",
        icon: "mdi-devices",
        submenu: [
            {
                url: "sku",
                title: "SKUs",
            },
            {
                url: "device-group",
                title: "Device Group",
            },
            {
                url: "messageLog",
                title: "Message Log",
            },
            {
                url: "device-logs",
                title: "Device Trace Logs",
                super: true,
            },
            {
                url: "downlinkQueues",
                title: "Downlink Queue",
            },
            {
                url: "device-configs",
                title: "Device Master Config  Files",
                params: {type: 'master'}
            },
            {
                url: "sim-card-types",
                title: "Sim Card Types"
            },
            {
                url: "sim-cards",
                title: "Sim Cards"
            },
            {
                url: "admin-ctc-readiness",
                title: "CARB Readiness",
                super: true
            },
            {
                url: "admin-ctc-submissions",
                title: "CARB Submissions",
                super: true
            },
            {
                url: "diagnostics",
                key: "diagnostics",
                title: "Diagnostics",
                submenu: [
                    {
                        url: "diagnostic-profiles",
                        title: "Profiles",
                    },
                    {
                        url: "diagnostic-payload-mapping",
                        title: "Mapping",
                    }
                ]
            }
        ]
    },
    {
        key: "personal_trackers",
        url: "personal-trackers",
        title: "Personal Trackers",
        icon: "mdi-map-marker-account",
    },
    {
        url: "harvesters",
        key: "harvesters",
        title: "Harvesters",
        icon: "mdi-solar-power-variant",
        submenu: [
            {
                url: "harvesters/sites",
                title: "Sites",
            }
        ]
    },
    {
        url: "ota-campaigns",
        key: "ota_campaigns",
        title: "Over The Air",
        icon: "mdi-antenna",
        submenu: [
            {
                url: "ota-packages",
                title: "Packages",
            },
            {
                url: "ota-files",
                title: "Files",
            },
        ]
    },
    {
        url: "gateways",
        key: "gateways",
        title: "Gateways",
        icon: "mdi-directions-fork"
    },
    {
        url: "companies",
        key: "companies",
        icon: "mdi-domain",
        title: "Companies",
        submenu: [
            {
                url: "locations",
                title: "Places, Locations and Zones"
            }
        ]
    },
    {
        url: "users",
        keys: "users",
        title: "Users",
        icon: "mdi-account-multiple",
        submenu: [
            {
                url: "roles",
                title: "Roles",
                icon: "mdi-account-badge",
            }, {
                url: "user-activity-logs",
                title: "User Activity Logs",
                icon: "mdi-account-badge",
            },
        ]
    },
    {
        url: "monitoring-configs",
        key: "monitoring",
        title: "Configurations",
        icon: "mdi-radar",
        submenu: [
            {
                url: "monitoring-templates",
                title: "Feature Setup",
            },
            {
                url: "monitoring-schedules",
                title: "Feature Schedule",
            },
            {
                url: "recipients",
                title: "Notifications",
            },
            {
                url: "configurations/wifi",
                title: "WIFI Access Points",
            }
        ]
    },
    {
        url: "summary-report",
        key: "reports",
        title: "Reports",
        icon: "mdi-chart-bar",
        submenu: [
            {
                url: "trips-report",
                title: "Trips",
            },
            {
                url: "trips-summary",
                title: "Trips Summary",
            },
            {
                url: "driver-report",
                title: "Driver Report",
            },
            {
                url: "reports/compliance",
                title: "Compliance Report",
            },
            {
                url: "",
                title: "Fleet Reports",
                submenu: [
                    {
                        title: "Asset Summary",
                        url: "fleet/reports/assets-summary",
                    },
                    {
                        title: "Asset Expenses",
                        url: "fleet/reports/assets-expense",
                    },
                    {
                        title: "Fuel Refill",
                        url: "fleet/reports/assets-fuel-refill",
                    },
                    {
                        title: "Scheduled Inspection Summary",
                        url: "fleet/reports/inspection-summary",
                    },
                    {
                        title: "Maintenance Cost Summary",
                        url: "fleet/reports/maintenance-summary",
                    },
                    {
                        title: "Vehicle Utilization",
                        url: "fleet/reports/vehicle-utilization",
                    },
                    {
                        title: "Scheduled Inspections & Maintenance",
                        url: "fleet/reports/inspection-maintenance",
                    },
                    {
                        title: "Driver Safety",
                        url: "fleet/reports/driver-safety",
                    },
                    {
                        title: "Expiring Documents",
                        url: "fleet/reports/expiring-documents",
                    },
                ]
            }
        ]
    },
    {
        url: "",
        key: "payments",
        title: "Payments",
        icon: "mdi-account-credit-card",
        submenu: [
            {
                url: "invoices",
                title: "Invoices",
            },
            {
                url: "subscription-plans",
                title: "Subscription Plans",
            },
            {
                url: "payment-methods",
                title: "Payment Methods",
            },
            {
                url: "payment-gateways",
                title: "Payment Gateways",
            },
            {
                url: "discount-schemes",
                title: "Discount Schemes",
            },
            {
                url: "discount-codes",
                title: "Discount Codes",
            },
        ]
    },
    {
        url: "",
        key: "fleet-management",
        title: "Fleet Management",
        icon: "mdi-car-multiple",
        submenu: [
            {
                url: "drivers",
                title: "Drivers",
            },
            {
                url: "fleet-dispatch",
                title: "Dispatch",
            },
            {
                url: "fleet/shifts",
                title: "Shifts",
            },
            {
                url: "",
                title: "Fleet Setup",
                submenu: [
                    {
                        url: "fleet/shift-settings",
                        title: "Shift Settings",
                    },
                    {
                        url: "fleet/manufacturers",
                        title: "Manufacturers",
                    },
                    {
                        url: "fleet/models",
                        title: "Models",
                    },
                    {
                        url: "fleet/statuses",
                        title: "Statuses",
                    },
                    {
                        url: "fleet/fuel-types",
                        title: "Fuel Types",
                    },
                    {
                        url: "fleet/expense-types",
                        title: "Expense Types",
                    },
                    {
                        url: "fleet/service-types",
                        title: "Inspection Types",
                        params: {type: "inspection"},
                    },
                    {
                        url: "fleet/service-types",
                        title: "Maintenance Service Types",
                        params: {type: "maintenance"},
                    },
                    {
                        url: "driver-payment-methods",
                        title: "Driver Payment Methods",
                    },
                ]
            },
        ]
    },
    {
        url: "",
        key: "business_reports",
        title: "Business Intelligence",
        icon: "mdi-chart-areaspline",
        submenu: [
            {
                url: "business-dashboard",
                title: "Executive Dashboard",
            },
            {
                url: "renewal-dashboard",
                title: "Renewal Dashboard",
                super: true,
            },
            {
                url: "revenue-report",
                title: "Revenue & Billing",
            },
            {
                url: "growth-report",
                title: "Growth & Signups",
            },
            {
                url: "operations-report",
                title: "Operations & Health",
            },
        ]
    },
    {
        url: "",
        key: "admin_config",
        title: "Admin Configs",
        icon: "mdi-account-cog",
        submenu: [
            {
                url: "portals",
                title: "Portals",
            },
            {
                url: "menu-groups",
                title: "Menu Groups",
            },
            {
                url: "preset-general-config",
                title: "Preset General Config",
            },
            {
                url: "legal-documents",
                title: "Legal Documents",
            }
        ]
    },
    {
        url: "",
        key: "system",
        title: "System",
        icon: "mdi-cog",
        submenu: [
            {
                url: "pending-companies",
                title: "Pending Companies",
                super: true,
            },
            {
                url: "admin-ips",
                title: "Admin IPs",
                super: true,
            },
            {
                url: "admin-announcements",
                title: "Announcements",
                super: true,
            },
            {
                url: "admin-verification-logs",
                title: "Verification Logs",
                super: true,
            },
            {
                url: "admin-app-installs",
                title: "App Installs",
                super: true,
            },
            {
                url: "admin-crm-integrations",
                title: "CRM Integrations",
                super: true,
            },
            {
                url: "settings",
                title: "System Settings",
            },
            {
                url: "system-tools",
                title: "Tools",
            }
        ]
    },
];

// Customer-facing menu. The server sends `menus`, the keys a role may see. An item
// is shown when its own key, or its nearest parent's key, is in `menus`; a group
// with no key is a plain container shown when any child is visible. Keys must stay
// stable; only order, titles and grouping change.
const user_menu = [
    {title: "Dashboard", icon: "mdi-view-dashboard", url: "dashboard", key: "dashboard", keywords: "home overview"},
    {title: "Live Map", icon: "mdi-broadcast", url: "live-view", key: "live_view", keywords: "map tracking where gps realtime location"},
    {
        title: "Activity",
        icon: "mdi-history",
        submenu: [
            {title: "Feed", url: "activity", key: "notifications", keywords: "activity alerts trips timeline"},
            {title: "Alert History", url: "notifications", key: "assets", keywords: "notifications alerts log"},
        ]
    },
    {
        key: "assets",
        title: "Vehicles",
        icon: "mdi-truck",
        submenu: [
            {title: "All Vehicles", url: "assets", keywords: "assets trucks cars vans fleet list"},
            {title: "Groups", url: "asset-groups"},
            {title: "Types", url: "asset-types"},
        ]
    },
    {
        key: "fleet-management",
        title: "Drivers & Dispatch",
        icon: "mdi-steering",
        submenu: [
            {title: "Drivers", url: "drivers", keywords: "staff employees invite"},
            {title: "Dispatch", url: "fleet-dispatch", keywords: "jobs routes stops"},
            {title: "Shifts", url: "fleet/shifts", keywords: "duty hours timesheet"},
            {title: "Inspections (DVIR)", url: "fleet/inspections", keywords: "dvir defects pre-trip post-trip walkaround"},
        ]
    },
    {
        key: "safety",
        title: "Safety",
        icon: "mdi-shield-account",
        submenu: [
            {title: "Overview", url: "fleet/reports/driver-safety", keywords: "safety scores harsh braking speeding"},
            {title: "Events Inbox", url: "fleet/reports/safety-inbox"},
            {title: "Leaderboard", url: "fleet/reports/safety-leaderboard"},
        ],
    },
    {title: "Compliance", icon: "mdi-leaf-circle-outline", url: "ctc-compliance", key: "compliance"},
    {
        key: "reports",
        title: "Reports",
        icon: "mdi-chart-bar",
        submenu: [
            {title: "Summary", url: "summary-report"},
            {
                title: "Trips & Drivers",
                submenu: [
                    {title: "Trips", url: "trips-report"},
                    {title: "Trips Summary", url: "trips-summary"},
                    {title: "Driver Report", url: "driver-report"},
                ]
            },
            {
                title: "Vehicles & Engine",
                submenu: [
                    {title: "Vehicle Utilization", url: "fleet/reports/vehicle-utilization"},
                    {title: "Asset Summary", url: "fleet/reports/assets-summary"},
                ]
            },
            {
                title: "Costs",
                submenu: [
                    {title: "Fuel Refill", url: "fleet/reports/assets-fuel-refill", keywords: "gas diesel"},
                    {title: "Asset Expenses", url: "fleet/reports/assets-expense"},
                    {title: "Maintenance Costs", url: "fleet/reports/maintenance-summary", keywords: "service repair"},
                ]
            },
            {
                title: "Inspections",
                submenu: [
                    {title: "Scheduled Inspection Summary", url: "fleet/reports/inspection-summary"},
                    {title: "Scheduled Inspections & Maintenance", url: "fleet/reports/inspection-maintenance"},
                ]
            },
            {
                title: "Documents & Compliance",
                submenu: [
                    {title: "Expiring Documents", url: "fleet/reports/expiring-documents"},
                    {title: "Compliance Report", url: "reports/compliance"},
                ]
            },
        ]
    },
    {
        title: "Alerts & Places",
        icon: "mdi-bell-ring-outline",
        submenu: [
            {title: "Alert Setup", url: "monitoring-configs", key: "monitoring", keywords: "alerts notifications rules speeding geofence"},
            {title: "Alert Rules", url: "monitoring-templates", key: "monitoring"},
            {title: "Places & Zones", url: "locations", key: "company", keywords: "geofence locations sites"},
            {title: "Schedules", url: "monitoring-schedules", key: "monitoring"},
            {title: "Recipients", url: "recipients", key: "monitoring"},
        ]
    },
    {
        title: "Settings",
        icon: "mdi-cog",
        submenu: [
            {
                title: "Devices & Hardware",
                key: "devices",
                submenu: [
                    {title: "Devices", url: "devices", keywords: "trackers hardware gps units"},
                    {title: "Gateways", url: "gateways"},
                ]
            },
            {title: "WiFi Access Points", url: "configurations/wifi", key: "monitoring"},
            {title: "Integrations", url: "integrations", key: "integrations"},
            {
                title: "Fleet Lists",
                key: "fleet-management",
                submenu: [
                    {title: "Shift Settings", url: "fleet/shift-settings"},
                    {title: "Inspection Checklists", url: "fleet/inspection-templates", keywords: "dvir checklist template items"},
                    {title: "Vehicle Statuses", url: "fleet/statuses"},
                    {title: "Fuel Types", url: "fleet/fuel-types"},
                    {title: "Expense Types", url: "fleet/expense-types"},
                    {title: "Inspection Types", url: "fleet/service-types", params: {type: "inspection"}},
                    {title: "Maintenance Types", url: "fleet/service-types", params: {type: "maintenance"}},
                    {title: "Driver Pay Methods", url: "driver-payment-methods"},
                ]
            },
            {title: "Safety Settings", url: "fleet/reports/coaching-settings", key: "safety"},
        ]
    },
    {
        title: "Account",
        icon: "mdi-domain",
        submenu: [
            {title: "Company Profile", url: "company-info", key: "company", keywords: "company name address time zone"},
            {title: "Users", url: "users", key: "company", keywords: "team members invite roles"},
            {
                title: "Billing",
                key: "company",
                submenu: [
                    {title: "Invoices", url: "invoices", companyAdmin: true, keywords: "billing payments"},
                    {title: "Payment Methods", url: "payment-methods", companyAdmin: true, keywords: "billing card"},
                ]
            },
            {title: "Delete Account", url: "company-account", key: "company", owner: true, keywords: "close cancel account"},
        ]
    },
];

// Partners are SUPER ADMIN only, and a super_admin does not take the 'admin'
// branch below - it would hand them user_menu and the entry would never appear.
// Appended here rather than added to admin_menu for that reason, and because the
// key filter would drop an item the server has no menu row for.
const resellerMenuItem = {
    title: "Partners",
    icon: "mdi-handshake-outline",
    url: "resellers",
    key: "resellers",
};

// Manager pages the API refuses the Fleet Driver role (RefuseFleetDriverRole).
// Hidden at any depth; the router guards the same pages by meta.notFleetDriver.
const FLEET_DRIVER_HIDDEN = ['fleet/reports/coaching-settings', 'fleet/reports/expiring-documents', 'fleet/shifts', 'fleet/shift-settings', 'fleet/inspections', 'fleet/inspection-templates'];

// Group headers only expand and collapse, so a header's own page becomes its first child.
const headerToChild = (items) => items.map(({url, params, ...item}) => {
    if (!item.submenu) return {url, params, ...item};
    const submenu = headerToChild(item.submenu);
    return {...item, submenu: url ? [{title: item.title, url, params}, ...submenu] : submenu};
});

const withKeys = (items, keys, parentKey) => items
    .filter(item => {
        const key = item.key ?? parentKey;
        return !key || keys.includes(key);
    })
    .map(item => item.submenu ? {...item, submenu: withKeys(item.submenu, keys, item.key ?? parentKey)} : item);

// Applies keep() at every depth, then drops groups left with no children.
const prune = (items, keep) => items
    .filter(keep)
    .map(item => item.submenu ? {...item, submenu: prune(item.submenu, keep)} : item)
    .filter(item => !item.submenu || item.submenu.length);

function buildMenu(user, menus) {
    const keys = menus ? Object.keys(menus) : null;
    let items;
    if (user?.super || user?.type === 'admin') {
        items = keys ? admin_menu.filter(item => keys.includes(item.key)) : admin_menu;
        if (user?.super) items = [...items, resellerMenuItem];
    } else {
        items = keys ? withKeys(user_menu, keys) : user_menu;
    }
    const isOwner = !!user?.id && user.id === user.company?.owner_id;
    const billingHidden = user?.type !== 'admin' && !isCompanyAdminRole(user);
    return prune(headerToChild(items), item =>
        !(user?.is_fleet_driver && FLEET_DRIVER_HIDDEN.includes(item.url))
        && !(item.super && !user?.super)
        && !(item.owner && !isOwner)
        && !(item.companyAdmin && billingHidden));
}

const filteredMenuItems = computed(() => buildMenu(user, menus));

// Sidebar search and the Ctrl+K palette match on the same permission-filtered leaves.
const searchQuery = ref('');

// Each leaf keeps the titles of the groups above it, shown as its subtitle.
const flattenLeaves = (items, acc = [], path = []) => {
    for (const item of items) {
        const group = path.filter(t => t && t !== item.title).join(' / ');
        if (item.url) acc.push({title: item.title, url: item.url, params: item.params, icon: item.icon, group, keywords: item.keywords || ''});
        if (item.submenu) flattenLeaves(item.submenu, acc, [...path, item.title]);
    }
    return acc;
};

const leafKey = (it) => it.url + JSON.stringify(it.params || {});

// Title matches first (starts-with before contains), then group and keyword matches.
const matchLeaves = (query) => {
    const q = (query || '').trim().toLowerCase();
    if (!q) return [];
    const seen = new Set();
    const scored = [];
    for (const it of flattenLeaves(filteredMenuItems.value)) {
        const title = (it.title || '').toLowerCase();
        const score = title.startsWith(q) ? 0
            : title.includes(q) ? 1
                : it.group.toLowerCase().includes(q) ? 2
                    : it.keywords.toLowerCase().includes(q) ? 3 : -1;
        if (score < 0 || seen.has(leafKey(it))) continue;
        seen.add(leafKey(it));
        scored.push({it, score});
    }
    return scored.sort((a, b) => a.score - b.score).map(x => x.it);
};

const searchResults = computed(() => matchLeaves(searchQuery.value));

// "Needs attention" counts, on the customer menu only. Feed uses the unread
// count every API response already carries.
const isStaffMenu = !!(user?.super || user?.type === 'admin');
const {counts: menuCounts} = useMenuCounts({enabled: !isStaffMenu});

const badgeFor = (item) => {
    if (isStaffMenu || !item?.url) return null;
    if (item.url === 'activity') {
        const n = unreadNotificationsCount.value;
        return n > 0 ? {count: n, severity: 'warning', label: n === 1 ? '1 unread alert' : `${n} unread alerts`} : null;
    }
    const badge = menuCounts.value[item.url];
    return badge?.count > 0 ? badge : null;
};

const groupBadge = (item) => combineMenuCounts(flattenLeaves(item.submenu ?? []).map(badgeFor));

const isOpen = (value) => openedModel.value.includes(value);

// With a count showing, open the page on the same filter the count used.
const goItem = (item) => {
    const to = badgeFor(item)?.to;
    if (!to) return go(item.url, item.params);
    router.push({name: to.name, query: to.query});
    if (isPhone.value) drawerOpen.value = false;
};

const isActive = (url, params = {}) => {
    if (Object.keys(params).length) {
        return (route.name === url || route.path === url) && Object.values(route.params).toString() === Object.values(params).toString()
    }
    return route.name === url || route.path === url;
};

// A group is marked when the open page is inside it, at any depth
const containsActive = (item) => (item.submenu ?? []).some(child => child.submenu ? containsActive(child) : isActive(child.url, child.params ?? {}));

const shortcutLabel = (typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform)) ? 'Cmd K' : 'Ctrl K';

// Shared with the TopBar search pill, which opens the same palette.
const paletteOpen = inject('paletteOpen', ref(false));
const paletteQuery = ref('');
const paletteIndex = ref(0);
const vehicleResults = ref([]);
const vehicleSearching = ref(false);
let vehicleTimer = null;
let vehicleSeq = 0;

// Vehicles are searched with the Assets list quick search, only for roles that can open that list.
const canSearchVehicles = computed(() => flattenLeaves(filteredMenuItems.value).some(l => l.url === 'assets'));

// The last pages opened from anywhere, per user, in this browser only.
const RECENT_MAX = 5;
const recentKey = `palette-recent:${user?.id ?? 'anon'}`;
const readRecent = () => {
    try {
        const list = JSON.parse(localStorage.getItem(recentKey) || '[]');
        return Array.isArray(list) ? list.filter(r => r && typeof r.url === 'string') : [];
    } catch {
        return [];
    }
};
const recentPages = ref(readRecent());
const rememberPage = () => {
    const leaf = flattenLeaves(filteredMenuItems.value).find(l => isActive(l.url, l.params ?? {}));
    if (!leaf) return;
    const entry = {url: leaf.url, params: leaf.params};
    const list = [entry, ...recentPages.value.filter(r => leafKey(r) !== leafKey(entry))].slice(0, RECENT_MAX);
    recentPages.value = list;
    try {
        localStorage.setItem(recentKey, JSON.stringify(list));
    } catch {
        // Storage full or blocked: recents just are not kept.
    }
};
watch(() => route.fullPath, rememberPage);

// Quick links shown before typing, when the role can open them.
const SHORTCUT_URLS = ['live-view', 'assets', 'fleet/inspections', 'monitoring-configs'];

const asPage = (p, section) => ({...p, kind: 'page', section, subtitle: p.group});

const jumpTo = computed(() => {
    const leaves = flattenLeaves(filteredMenuItems.value);
    const byKey = new Map(leaves.map(l => [leafKey(l), l]));
    const recent = recentPages.value.map(r => byKey.get(leafKey(r))).filter(Boolean);
    const taken = new Set(recent.map(leafKey));
    const shortcuts = SHORTCUT_URLS
        .map(url => leaves.find(l => l.url === url && !Object.keys(l.params || {}).length))
        .filter(l => l && !taken.has(leafKey(l)));
    return [...recent.map(p => asPage(p, 'Recent')), ...shortcuts.map(p => asPage(p, 'Pages'))];
});

const paletteResults = computed(() => {
    if (!(paletteQuery.value || '').trim()) return jumpTo.value;
    return [
        ...matchLeaves(paletteQuery.value).slice(0, 8).map(p => asPage(p, 'Pages')),
        ...vehicleResults.value.map(a => ({
            kind: 'vehicle',
            section: 'Vehicles',
            title: a.label || a.slno || `Vehicle ${a.id}`,
            subtitle: [a.license_plate, a.slno].filter(Boolean).join(' / '),
            icon: 'mdi-truck',
            url: 'asset',
            params: {asset: a.id},
        })),
    ];
});

const paletteLiveText = computed(() => {
    if (vehicleSearching.value) return 'Searching vehicles';
    if (!paletteQuery.value) return '';
    const n = paletteResults.value.length;
    return n ? `${n} ${n === 1 ? 'result' : 'results'}` : 'No matches';
});

// Keep the highlighted row in view while moving with the arrow keys.
watch(paletteIndex, (i) => nextTick(() => {
    document.getElementById('palette-opt-' + i)?.scrollIntoView?.({block: 'nearest'});
}));

watch(paletteQuery, (value) => {
    paletteIndex.value = 0;
    clearTimeout(vehicleTimer);
    vehicleResults.value = [];
    const q = (value || '').trim();
    const seq = ++vehicleSeq;
    if (q.length < 2 || !canSearchVehicles.value) {
        vehicleSearching.value = false;
        return;
    }
    vehicleSearching.value = true;
    vehicleTimer = setTimeout(() => {
        send('get', 'assets', {quick_search: q, per_page: 5, page: 1})
            .then((response) => {
                if (seq === vehicleSeq) vehicleResults.value = response.data?.data?.assets?.data ?? [];
            })
            .catch(() => {
                if (seq === vehicleSeq) vehicleResults.value = [];
            })
            .finally(() => {
                if (seq === vehicleSeq) vehicleSearching.value = false;
            });
    }, 300);
});

watch(paletteOpen, (open) => {
    paletteIndex.value = 0;
    if (!open) paletteQuery.value = '';
});

const choosePaletteItem = (item) => {
    if (!item) return;
    paletteOpen.value = false;
    go(item.url, item.params);
};

const onPaletteKey = (e) => {
    const count = paletteResults.value.length;
    if (e.key === 'ArrowDown' && count) {
        e.preventDefault();
        paletteIndex.value = (paletteIndex.value + 1) % count;
    } else if (e.key === 'ArrowUp' && count) {
        e.preventDefault();
        paletteIndex.value = (paletteIndex.value - 1 + count) % count;
    } else if (e.key === 'Enter') {
        e.preventDefault();
        choosePaletteItem(paletteResults.value[paletteIndex.value]);
    }
};

const onShortcut = (e) => {
    if ((e.ctrlKey || e.metaKey) && !e.altKey && !e.shiftKey && e.key?.toLowerCase() === 'k') {
        e.preventDefault();
        paletteOpen.value = !paletteOpen.value;
    }
};

onMounted(() => {
    // Open the groups that hold the current page
    if (!effectiveRail.value) {
        openedGroups.value = filteredMenuItems.value.flatMap((item, i) => !containsActive(item) ? [] : [
            'g' + i, ...(item.submenu ?? []).map((sub, j) => containsActive(sub) ? 'g' + i + '-' + j : null).filter(Boolean),
        ]);
    }
    window.addEventListener('keydown', onShortcut);
    document.addEventListener('pointerdown', onOutsidePointer);
    rememberPage();
});

onUnmounted(() => {
    clearTimeout(peekTimer);
    clearTimeout(vehicleTimer);
    window.removeEventListener('keydown', onShortcut);
    document.removeEventListener('pointerdown', onOutsidePointer);
});
</script>

<style>
#sidebar .sidebar-toggle-toolbar button.active {
    background-color: rgba(var(--v-theme-on-surface), 0.06);
}

#sidebar .v-list-item--active {
    background-color: rgba(var(--v-theme-primary), 0.12) !important;
}

#sidebar .v-list-item--active, #sidebar .v-list-item--active .v-list-item-title {
    font-weight: bold;
    color: rgb(var(--v-theme-on-surface));
}

#sidebar {
    transition: width 0.15s ease;
}

#sidebar .group-has-active .v-list-item-title {
    font-weight: bold;
}

#sidebar .shortcut-hint {
    cursor: pointer;
}

.v-main {
    transition: padding-inline-start 0.15s ease;
}
</style>
